import React, { Fragment, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Typography, Box, TextField, Chip, Stack, FormControl, InputLabel, Select, MenuItem, InputAdornment, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, Tooltip, CircularProgress, useMediaQuery } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import AccountCard from "../components/AccountCard";
import styled from "@emotion/styled";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getAccounts, updateAccount } from "../redux/ducks/account";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useHasPermission } from "../utils/permissionUtils";
import PageHeader from "../components/PageHeader";
import { setUserPreferences } from "../redux/slices/auth";
import { updateUser, getManagers } from "../redux/slices/users";
import ViewToggle from "../components/ViewToggle";
import UserMenuChip from "../components/UserMenuChip";
import UserAssignmentDialog from "../components/UserAssignmentDialog";
import ActiveChip from "../components/ActiveChip";
import { statusColor } from "../utils/statusColors";

const ContainerStyled = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
    padding: theme.spacing(2),
}));

const HeaderFilters = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "2fr 1fr 1fr",
    },
}));

const SearchField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: theme.shape.borderRadius * 4,
        background: theme.palette.background.paper,
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
        },
    },
}));

const SelectFilter = styled(Select)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 4,
    background: theme.palette.background.paper,
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
    },
}));

const NoResults = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    background: theme.palette.background.paper,
}));

const GridContainer = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
        gap: 16,
    },
}));

const StatsChip = styled(Chip)(({ theme }) => ({
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.light,
    fontWeight: theme.typography.fontWeightSemiBold,
    "& .MuiChip-label": {
        paddingX: 12,
    },
}));

const PaginationBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(4),
}));

const SearchAdornmentIcon = styled(SearchIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const NoResultsHeading = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StyledPagination = styled(Pagination)({
    "& .MuiPaginationItem-root": {
        borderRadius: 2,
    },
});

const NameCell = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    width: "fit-content",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const PrivacyDot = styled(Box)(({ theme }) => ({
    width: theme.spacing(1.5),
    minWidth: theme.spacing(1.5),
    height: theme.spacing(1.5),
    borderRadius: "50%",
}));

const ClientLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
        textDecoration: "underline",
    },
}));

const ActiveCell = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
}));

const BillingCell = styled(Box)({
    display: "flex",
    flexDirection: "column",
});

const AccountList = ({ client = null, pinned = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { clientSlug = "" } = useParams();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [billingFilter, setBillingFilter] = useState("all");
    const uniqueBillingTerms = ["Retainer", "Hourly", "Fixed Price", "Internal", "Non Billable"];
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 18;
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const fetchingRef = useRef(false);
    const limit = 0;
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const currentUser = useSelector((state) => state.auth.currentUser);
    const userId = useSelector((state) => state.auth.userId);
    const [view, setView] = useState(() => currentUser?.preferences?.UI?.accountsView || "card");
    const [isUserAssignmentOpen, setIsUserAssignmentOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [managers, setManagers] = useState([]);

    const handleViewChange = (newView) => {
        setView(newView);
        if (!userId) return;
        const preferences = {
            ...(currentUser?.preferences || {}),
            UI: {
                ...(currentUser?.preferences?.UI || {}),
                accountsView: newView,
            },
        };
        dispatch(setUserPreferences({ preferences }));
        dispatch(updateUser({ userId, userData: { preferences }, silent: true }));
    };

    const fetchAccounts = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setLoading(true);

        dispatch(
            getAccounts(0, limit, clientSlug, pinned, (data) => {
                if (data && Array.isArray(data) && data.length === 1) {
                    navigate(`/account/${data[0].slug || data[0]._id}`);
                }
                setAccounts(Array.isArray(data) ? data : []);
                setLoading(false);
                fetchingRef.current = false;
            })
        );
    }, [clientSlug, pinned, dispatch]);

    useEffect(() => {
        setAccounts([]);
        fetchAccounts();
        // eslint-disable-next-line
    }, [clientSlug, pinned]);

    useEffect(() => {
        if (canSeeInternal) {
            dispatch(getManagers({
                callback: (data) => {
                    if (data && Array.isArray(data)) {
                        setManagers(data);
                    }
                }
            }));
        }
    }, [dispatch, canSeeInternal]);

    const handleUserAssignmentClose = () => {
        setIsUserAssignmentOpen(false);
    };

    const HandleReAssignAccountManager = (accountId) => {
        setSelectedAccount(accountId);
        setIsUserAssignmentOpen(true);
    };

    const handleAccountManagerAssign = (accountId, assigned, responsible) => {
        dispatch(
            updateAccount(accountId, { accountManager: responsible._id }, (response) => {
                if (response?.account) {
                    setAccounts((prev) => prev.map((account) =>
                        account._id === accountId
                            ? { ...account, accountManager: responsible }
                            : account
                    ));
                }
            })
        );
        setIsUserAssignmentOpen(false);
    };

    const filteredAccounts = useMemo(() => {
        return accounts.filter((account) => {
            const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase()) || account.client.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" || account.active === (statusFilter === "active" ? true : false);
            const matchesBilling = billingFilter === "all" || account.billingTerms.type.toLowerCase() === billingFilter.toLowerCase();

            return matchesSearch && matchesStatus && matchesBilling;
        });
    }, [accounts, searchTerm, statusFilter, billingFilter]);

    const paginatedAccounts = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAccounts.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAccounts, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);

    return (
        <ContainerStyled>
            {canSeeInternal && (
                <PageHeader
                    title="Accounts"
                    actions={
                        <Stack direction="row" spacing={2}>
                            <StatsChip label={`${accounts.length} Accounts`} />
                        </Stack>
                    }
                    filters={
                        <HeaderFilters>
                            <SearchField
                                fullWidth
                                placeholder="Search accounts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchAdornmentIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <SelectFilter value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </SelectFilter>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Billing</InputLabel>
                                <SelectFilter value={billingFilter} label="Billing" onChange={(e) => setBillingFilter(e.target.value)}>
                                    <MenuItem value="all">All Billing</MenuItem>
                                    {uniqueBillingTerms.map((term) => (
                                        <MenuItem key={term} value={term.toLowerCase()}>
                                            {term}
                                        </MenuItem>
                                    ))}
                                </SelectFilter>
                            </FormControl>
                        </HeaderFilters>
                    }
                />
            )}

            {loading ? (
                <NoResults>
                    <CircularProgress />
                </NoResults>
            ) : filteredAccounts.length === 0 ? (
                <NoResults>
                    <NoResultsHeading variant="h6" color="text.secondary">
                        No accounts found
                    </NoResultsHeading>
                    <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filter criteria
                    </Typography>
                </NoResults>
            ) : (
                <Fragment key={currentPage}>
                    {!isMobile && <ViewToggle view={view} onChange={handleViewChange} />}

                    {(isMobile || view === "card") ? (
                        <GridContainer>
                            {paginatedAccounts.map((account) => (
                                <AccountCard key={account._id} account={account} HandleReAssignAccountManager={HandleReAssignAccountManager} />
                            ))}
                        </GridContainer>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Account Manager</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Billing</TableCell>
                                        <TableCell>Active</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedAccounts.map((account) => (
                                        <TableRow key={account._id} hover>
                                            <TableCell>
                                                <NameCell onClick={() => navigate(`/account/${account.slug}`)}>
                                                    {account.privacy !== "Public" && (
                                                        <Tooltip title={account.privacy}>
                                                            <PrivacyDot bgcolor={statusColor(theme, "privacy", account.privacy)} />
                                                        </Tooltip>
                                                    )}
                                                    {account.name}
                                                </NameCell>
                                            </TableCell>
                                            <TableCell>
                                                {account.client?.slug ? (
                                                    <ClientLink onClick={() => navigate(`/client/${account.client.slug}`)}>{account.client.name}</ClientLink>
                                                ) : (
                                                    account.client?.name || "—"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <UserMenuChip user={account.accountManager} menuItems={{ reAssign: true }} onReAssign={() => HandleReAssignAccountManager(account._id)} />
                                            </TableCell>
                                            <TableCell>{account.status}</TableCell>
                                            <TableCell>
                                                <BillingCell>
                                                    {account.billingTerms.type}
                                                    {account.billingTerms.type === "Retainer" && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {account.billingTerms.hours} Hrs {account.billingTerms.frequency} / {account.billingTerms.overage ? "Overage Allowed" : "No Overage"}
                                                        </Typography>
                                                    )}
                                                </BillingCell>
                                            </TableCell>
                                            <TableCell>
                                                <ActiveCell>
                                                    {account.client?.active === false && account.active && (
                                                        <Chip color="warning" variant="outlined" size="small" label="Client Inactive" />
                                                    )}
                                                    <ActiveChip status={account.active ? "Active" : "Inactive"} />
                                                </ActiveCell>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {totalPages > 1 && (
                        <PaginationBox>
                            <StyledPagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(_, page) => setCurrentPage(page)}
                                color="primary"
                                size="large"
                            />
                        </PaginationBox>
                    )}

                    <UserAssignmentDialog
                        isOpen={isUserAssignmentOpen}
                        onClose={handleUserAssignmentClose}
                        objectId={selectedAccount}
                        objectType="account"
                        onAssign={handleAccountManagerAssign}
                        isNewObject={true}
                        users={managers}
                        singleSelection={true}
                    />
                </Fragment>
            )}
        </ContainerStyled>
    );
};

export default AccountList;
